// Модель «Загрузки кухни» для экрана: закрытые (OLAP) + открытые (живые данные) по часам и цехам.
// Единое правило: итог = закрытые + открытые — для позиций, заказов и суммы, везде (столбец, панель
// часа, легенда, карточки сводки, пиковый час). Открытые учитываются только когда includeOpen
// (период включает сегодня и режим не «в среднем за день»).
//
// Цех открытой позиции берётся из живых данных (бэкенд сопоставляет блюдо со справочником):
// - цех есть среди мест графика — в его заштрихованную часть;
// - цеха нет среди мест графика (или он ушёл в «Прочее») — в «Прочее»;
// - «Не найдено» (новое блюдо) и «Без места» (цех не задан) — в нейтральный блок «Без цеха».
//
// Двойной счёт на стыке не сверяется: в OLAP-агрегате нет id заказов (см. обсуждение задачи).

export const NO_PLACE = 'Без места';
export const NOT_FOUND = 'Не найдено';
export const OTHER = 'Прочее';

export const hourOrder = (hour) => (hour - 5 + 24) % 24; // учётные сутки с 05:00, как на бэкенде

const hourLabel = (hour) => `${String(hour).padStart(2, '0')}:00`;

function emptyHour(hour) {
  return {
    hour,
    label: hourLabel(hour),
    closed: {}, // цех → позиции закрытых
    open: {}, // цех → позиции открытых
    noCook: { notFound: 0, noPlace: 0 }, // открытые без цеха
    closedItems: 0,
    openItems: 0,
    closedOrders: 0,
    openOrders: 0,
    closedSum: 0,
    openSum: 0,
  };
}

export function buildKitchenModel({ block, places, live, includeOpen }) {
  const placeList = (places || []).map((p) => ({ ...p }));
  const known = new Set(placeList.map((p) => p.name));
  const ensureOther = () => {
    if (!known.has(OTHER)) {
      placeList.push({ name: OTHER, color_index: null });
      known.add(OTHER);
    }
    return OTHER;
  };

  const byHour = new Map();
  const at = (hour) => {
    if (!byHour.has(hour)) byHour.set(hour, emptyHour(hour));
    return byHour.get(hour);
  };

  (block?.hours || []).forEach((h) => {
    const entry = at(h.hour);
    entry.closed = { ...h.places };
    entry.closedItems = h.items;
    entry.closedOrders = h.orders;
    entry.closedSum = h.sum;
  });

  const openTotals = { items: 0, orders: 0, sum: 0 };
  if (includeOpen && live?.available) {
    (live.orders || []).forEach((order) => {
      const hour = Number(String(order.opened || '').slice(11, 13));
      if (!Number.isInteger(hour)) return;
      const entry = at(hour);
      entry.openOrders += 1;
      entry.openSum += order.sum;
      openTotals.orders += 1;
      openTotals.sum += order.sum;
      Object.entries(order.places || {}).forEach(([name, count]) => {
        entry.openItems += count;
        openTotals.items += count;
        if (name === NOT_FOUND) entry.noCook.notFound += count;
        else if (name === NO_PLACE) entry.noCook.noPlace += count;
        else {
          const key = known.has(name) && name !== OTHER ? name : ensureOther();
          entry.open[key] = (entry.open[key] || 0) + count;
        }
      });
    });
  }

  // Часы по учётным суткам, пропуски внутри диапазона — нулями
  const hours = [];
  if (byHour.size) {
    const positions = [...byHour.keys()].map(hourOrder);
    for (let position = Math.min(...positions); position <= Math.max(...positions); position += 1) {
      const hour = (position + 5) % 24;
      const entry = byHour.get(hour) || emptyHour(hour);
      hours.push({
        ...entry,
        items: entry.closedItems + entry.openItems,
        orders: entry.closedOrders + entry.openOrders,
        sum: entry.closedSum + entry.openSum,
        noCookTotal: entry.noCook.notFound + entry.noCook.noPlace,
      });
    }
  }

  const totalsByPlace = {};
  const openByPlace = {};
  hours.forEach((h) => {
    Object.entries(h.closed).forEach(([name, n]) => {
      totalsByPlace[name] = (totalsByPlace[name] || 0) + n;
    });
    Object.entries(h.open).forEach(([name, n]) => {
      totalsByPlace[name] = (totalsByPlace[name] || 0) + n;
      openByPlace[name] = (openByPlace[name] || 0) + n;
    });
  });

  // Больше позиций за период (с открытыми) — ниже в столбце и раньше в легенде
  const orderedPlaces = placeList
    .filter((p) => (totalsByPlace[p.name] || 0) > 0)
    .map((p) => ({ ...p, total: totalsByPlace[p.name] || 0, open: openByPlace[p.name] || 0 }))
    .sort((a, b) => b.total - a.total);

  const noCook = hours.reduce(
    (acc, h) => ({ notFound: acc.notFound + h.noCook.notFound, noPlace: acc.noPlace + h.noCook.noPlace }),
    { notFound: 0, noPlace: 0 }
  );

  const closedTotals = block?.totals || { items: 0, orders: 0, sum: 0 };
  const totals = {
    items: closedTotals.items + openTotals.items,
    orders: closedTotals.orders + openTotals.orders, // закрытые — из итога OLAP (дробные части складываются)
    sum: closedTotals.sum + openTotals.sum,
  };

  const peak = hours.reduce((best, h) => (h.items > (best ? best.items : 0) ? h : best), null);

  return {
    hours,
    places: orderedPlaces,
    totals,
    openTotals,
    noCook: { ...noCook, total: noCook.notFound + noCook.noPlace },
    peak,
  };
}
